// ContractTabBar — the adaptive (Priority+) tab bar for the contract-detail
// page. Renders the contract-section tabs in editorial order, collapsing those
// that do not fit into an inline overflow row behind a More button. The hidden
// TabMeasureRow feeds useAdaptiveTabFit + splitTabFit; until widths are
// measurable (jsdom / pre-layout) every tab shows and there is no More button.
//
// Open/close (locked): the overflow row opens on a More click or when the active
// tab is collapsed, stays open while moving between overflow tabs, and closes on
// a primary-tab click, the × button, or Escape (focus returns to More). While
// OPEN the canonical split renders (no pull-forward) so a just-clicked overflow
// tab does not jump rows; while CLOSED a hidden active tab is pulled forward into
// the visible row so the selected tab is always reachable.
//
// ARIA: one role="tablist" spans both rows (row containers role=presentation).
// Arrow-key roving covers the visible tabs plus (when open) the overflow tabs;
// More and × stay in Tab order but are skipped by arrow roving (per APG). The
// measurement row is a SIBLING of the tablist, aria-hidden, never active, with
// measure-prefixed ids — so exactly one aria-selected="true" and no duplicate
// ids exist in the document.

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { TabButton } from "../../common";
import {
  CONTRACT_TABS,
  TAB_SLUGS,
  getTabDef,
  type TabSlug,
} from "./tabRegistry";
import { useAdaptiveTabFit } from "../../../hooks/useAdaptiveTabFit";
import { splitTabFit, splitTabFitCanonical } from "./splitTabFit";
import MoreSectionsButton from "./MoreSectionsButton";
import OverflowTabRow from "./OverflowTabRow";
import TabMeasureRow from "./TabMeasureRow";
import TabLabelWithAttention from "./TabLabelWithAttention";

// Fallback split used before widths are measurable: every tab visible, no
// overflow. Stable module-level value so it never re-triggers memoised derivations.
const ALL_VISIBLE_SPLIT = {
  visibleIndices: TAB_SLUGS.map((_, i) => i),
  overflowIndices: [] as number[],
  pulledForward: false,
};

interface ContractTabBarProps {
  activeTab: TabSlug;
  // `replace: true` signals the activation should replace the current history
  // entry (keyboard roving) rather than push a new one (click activation).
  onTabChange: (tab: TabSlug, options?: { replace?: boolean }) => void;
  badgeCounts: Partial<Record<TabSlug, number | undefined>>;
  attention: Partial<Record<TabSlug, boolean>>;
}

export default function ContractTabBar({
  activeTab,
  onTabChange,
  badgeCounts,
  attention,
}: ContractTabBarProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The live primary row — its computed column-gap (gap-8) feeds the fit math.
  const rowRef = useRef<HTMLDivElement | null>(null);
  const measureItemRefs = useRef<Array<HTMLElement | null>>([]);
  const measureMoreRef = useRef<HTMLButtonElement | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  // Real (non-measurement) tab buttons, keyed by slug because indices shift as
  // tabs move between the visible and overflow rows.
  const tabButtonRefs = useRef<Map<TabSlug, HTMLButtonElement | null>>(
    new Map(),
  );

  // Tab widths change with the badge digit count and with the attention dot
  // (which adds real width), so the measurement must re-run when either changes.
  const remeasureKey = TAB_SLUGS.map(
    (s) => `${badgeCounts[s] ?? ""}:${attention[s] ? "!" : ""}`,
  ).join("|");

  const { itemWidths, moreWidth, containerWidth, gapWidth, measured } =
    useAdaptiveTabFit({
      containerRef,
      rowRef,
      itemRefs: measureItemRefs,
      moreRef: measureMoreRef,
      itemCount: CONTRACT_TABS.length,
      remeasureKey,
    });

  const activeIndex = TAB_SLUGS.indexOf(activeTab);

  const [rowOpen, setRowOpen] = useState(false);

  // The canonical (no pull-forward) split tells us which tabs are conceptually
  // collapsed — used to decide whether the active tab should auto-open the row.
  const canonical = useMemo(
    () =>
      measured
        ? splitTabFitCanonical({
            itemWidths,
            moreWidth,
            containerWidth,
            gap: gapWidth,
            activeIndex,
          })
        : ALL_VISIBLE_SPLIT,
    [measured, itemWidths, moreWidth, containerWidth, gapWidth, activeIndex],
  );

  const activeInCanonicalOverflow =
    canonical.overflowIndices.includes(activeIndex);

  // Open/close is mostly user-controlled (More toggle, ×, Escape) but must also
  // react when the auto-open decision changes: landing on a collapsed tab (or a
  // deep link that only resolves to "collapsed" once widths are measured) opens
  // the row; navigating to a primary tab closes it. Rather than a
  // setState-in-effect (which the lint rules forbid as a cascading render),
  // derive the correction during render. The decision key folds in everything
  // the auto-open depends on — active tab, whether measured, and whether the
  // active tab is canonically collapsed. A user toggle (More/×/Escape) changes
  // none of those, so it never re-fires this guard and is preserved.
  const autoOpenKey = `${activeTab}|${measured}|${activeInCanonicalOverflow}`;
  const [lastAutoOpenKey, setLastAutoOpenKey] = useState<string | null>(null);
  if (lastAutoOpenKey !== autoOpenKey) {
    setLastAutoOpenKey(autoOpenKey);
    setRowOpen(measured && activeInCanonicalOverflow);
  }

  const hasOverflow = canonical.overflowIndices.length > 0;
  const forcedClosed = !measured || !hasOverflow;

  // The effective split: canonical while open (so a clicked overflow tab does
  // not jump rows), pull-forward while closed (so a hidden active tab stays
  // reachable).
  const split = useMemo(() => {
    if (!measured) return ALL_VISIBLE_SPLIT;
    const args = {
      itemWidths,
      moreWidth,
      containerWidth,
      gap: gapWidth,
      activeIndex,
    };
    return rowOpen && !forcedClosed
      ? splitTabFitCanonical(args)
      : splitTabFit(args);
  }, [
    measured,
    itemWidths,
    moreWidth,
    containerWidth,
    gapWidth,
    activeIndex,
    rowOpen,
    forcedClosed,
  ]);

  const visibleSlugs = useMemo(
    () => split.visibleIndices.map((i) => TAB_SLUGS[i]),
    [split],
  );
  const overflowSlugs = useMemo(
    () => split.overflowIndices.map((i) => TAB_SLUGS[i]),
    [split],
  );

  const open = rowOpen && !forcedClosed && overflowSlugs.length > 0;

  // The dot lights when any tab that is actually HIDDEN (in the overflow row)
  // needs attention. A pulled-forward active tab is visible, so it never counts.
  const hasHiddenAttention = overflowSlugs.some((slug) => attention[slug]);

  const closeRow = useCallback(() => {
    setRowOpen(false);
    moreButtonRef.current?.focus();
  }, []);

  const registerTab = useCallback(
    (slug: TabSlug) => (el: HTMLButtonElement | null) => {
      tabButtonRefs.current.set(slug, el);
    },
    [],
  );

  // A primary-tab click closes the row; an overflow-tab click keeps it open.
  const handleTabClick = useCallback(
    (slug: TabSlug, fromOverflow: boolean) => {
      onTabChange(slug);
      if (!fromOverflow) setRowOpen(false);
    },
    [onTabChange],
  );

  // Roving focus across the visible tabs plus, when open, the overflow tabs.
  // More and × stay in normal Tab order but are skipped here (per APG).
  const rovingSlugs = useMemo<TabSlug[]>(
    () => (open ? [...visibleSlugs, ...overflowSlugs] : visibleSlugs),
    [open, visibleSlugs, overflowSlugs],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        if (open) {
          event.preventDefault();
          closeRow();
        }
        return;
      }
      const currentIndex = rovingSlugs.indexOf(activeTab);
      // If focus is on a tab not in the roving set (shouldn't happen), bail.
      let targetIndex: number | null = null;
      switch (event.key) {
        case "ArrowRight":
          targetIndex =
            currentIndex === -1
              ? 0
              : (currentIndex + 1) % rovingSlugs.length;
          break;
        case "ArrowLeft":
          targetIndex =
            currentIndex === -1
              ? rovingSlugs.length - 1
              : (currentIndex - 1 + rovingSlugs.length) % rovingSlugs.length;
          break;
        case "Home":
          targetIndex = 0;
          break;
        case "End":
          targetIndex = rovingSlugs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      const targetSlug = rovingSlugs[targetIndex];
      // Roving activation replaces the history entry so arrow-keying across
      // the tab bar doesn't flood browser history with one entry per tab.
      onTabChange(targetSlug, { replace: true });
      requestAnimationFrame(() => {
        tabButtonRefs.current.get(targetSlug)?.focus();
      });
    },
    [activeTab, closeRow, onTabChange, open, rovingSlugs],
  );

  return (
    <div
      ref={containerRef}
      className="relative border-b border-slate-200 dark:border-slate-700"
    >
      {/* One tablist spanning the visible primary row and (when open) the
          inline overflow row. The hidden measurement row is a SIBLING of the
          tablist, not a child, so it never inflates the tab count exposed to
          assistive tech. */}
      <div role="tablist" aria-label="Contract sections" onKeyDown={handleKeyDown}>
        {/* Visible primary row + the More button. Its gap-8 is the measured
            source of the fit math's inter-tab gap (TabMeasureRow mirrors the
            same class so measured widths match). */}
        <div ref={rowRef} role="presentation" className="flex items-center gap-8">
          {visibleSlugs.map((slug) => {
            const { label, icon: Icon } = getTabDef(slug);
            return (
              <TabButton
                key={slug}
                ref={registerTab(slug)}
                active={activeTab === slug}
                panelId={`${slug}-panel`}
                icon={<Icon className="h-4 w-4" />}
                badge={badgeCounts[slug]}
                onClick={() => handleTabClick(slug, false)}
              >
                <TabLabelWithAttention label={label} attention={attention[slug]} />
              </TabButton>
            );
          })}
          {!forcedClosed && overflowSlugs.length > 0 && (
            <MoreSectionsButton
              ref={moreButtonRef}
              hiddenCount={overflowSlugs.length}
              open={open}
              hasAttention={hasHiddenAttention}
              onClick={() => setRowOpen((v) => !v)}
            />
          )}
        </div>

        {open && (
          <OverflowTabRow
            slugs={overflowSlugs}
            activeTab={activeTab}
            badgeCounts={badgeCounts}
            attention={attention}
            onTabClick={(slug) => handleTabClick(slug, true)}
            onClose={closeRow}
            registerTab={registerTab}
          />
        )}
      </div>

      <TabMeasureRow
        badgeCounts={badgeCounts}
        attention={attention}
        itemRefs={measureItemRefs}
        moreRef={measureMoreRef}
      />
    </div>
  );
}
